import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { Translate } from 'react-localize-redux';
import { type RouteComponentProps, Switch } from 'react-router';

import LazyComponent from '@fd/ui/LazyComponent';

import { toKebabCase } from '../../helpers/strings';
import { lazyWithRetry } from '../../helpers/utilities';
import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { flagService } from '../../services/flagService/flagService';
import { PortalViewId, ViewRoute } from '../../viewConfiguration';
import DeliverySettingsPage from '../DeliverySettings/DeliverySettingsPage';
import RestaurantLayoutRoutes from '../RestaurantLayout/RestaurantLayout.routes';
import { RMSModules } from '../RMS/utils/rmsModules';
import { RMSSubscriptions } from '../RMS/utils/rmsSubscriptions';
import DangerZoneList from './components/DangerZoneList';
import PropertyGeneralSettingsPage from './PropertyGeneralSettingsPage';
import PropertyManagement from './PropertyManagement';
import PropertyOpeningHoursPage from './PropertyOpeningHoursPage';

const RMSPage = lazyWithRetry(() => import('../RMS/components/RMSPage'));

type RouteParams = {
  appId?: string;
  propertyId?: string;
};

type Props = RouteComponentProps<RouteParams>;

const PropertySettingsRoutes: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const {
    match: { path, params },
  } = props;
  const toParentFromSettingsIframe = `/${params?.appId}/properties/${params?.propertyId}?tab=settings`;
  const basePropertyIframeUrl = 'configurations/franchisors/properties';
  const isShowCollectionSettingsAtPropertyLevelOn = useAppSelector((state) =>
    flagService.isFlagOn(state, 'showCollectionSettingsAtPropertyLevel')
  );
  return (
    <ErrorBoundary identifier="property-settings">
      <Switch>
        <ViewRoute
          exact
          path={`${path}/general-settings`}
          render={(routeProps) => <PropertyGeneralSettingsPage {...(routeProps as any)} />}
        />
        <ViewRoute
          exact
          path={`${path}/${toKebabCase(RMSModules.OPERATING_CONFIGURATION)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                toParent={toParentFromSettingsIframe}
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Operating_Configuration" />}
                module={RMSModules.OPERATING_CONFIGURATION}
                url={`${basePropertyIframeUrl}/${RMSModules.OPERATING_CONFIGURATION}`}
                propertyIdFromUrl
              />
            </LazyComponent>
          )}
        />
        <ViewRoute
          exact
          path={`${path}/layout`}
          render={(routeProps) => <RestaurantLayoutRoutes {...routeProps} />}
        />
        <ViewRoute
          exact
          path={`${path}/${toKebabCase(RMSModules.GEO_CONFIGURATION)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Geo_Configuration" />}
                module={RMSModules.GEO_CONFIGURATION}
                url={`configurations/franchisors/properties/${RMSModules.GEO_CONFIGURATION}`}
                propertyIdFromUrl
                toParent={toParentFromSettingsIframe}
              />
            </LazyComponent>
          )}
        />
        <ViewRoute
          exact
          path={`${path}/${toKebabCase(RMSModules.DELIVERY_CONFIGURATION)}`}
          render={() => (
            <DeliverySettingsPage
              backUrl={toParentFromSettingsIframe}
              legacy={
                <LazyComponent>
                  <RMSPage
                    subscription={RMSSubscriptions.BASE}
                    title={<Translate id="Delivery_Management_Configuration" />}
                    module={RMSModules.DELIVERY_CONFIGURATION}
                    url={`configurations/franchisors/properties/${RMSModules.DELIVERY_CONFIGURATION}`}
                    propertyIdFromUrl
                    toParent={toParentFromSettingsIframe}
                  />
                </LazyComponent>
              }
            />
          )}
        />
        {/* Address Mapping */}
        <ViewRoute
          exact
          path={`${path}/${toKebabCase(RMSModules.ADDRESS_MAPPING)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Address_Mapping" />}
                module={RMSModules.ADDRESS_MAPPING}
                url={`configurations/system/${RMSModules.ADDRESS_MAPPING}`}
                toParent={toParentFromSettingsIframe}
                propertyIdFromUrl
              />
            </LazyComponent>
          )}
        />
        <ViewRoute
          exact
          path={`${path}/${toKebabCase(RMSModules.FLOAT_CONFIGURATION)}`}
          render={() => (
            <LazyComponent>
              <RMSPage
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Float_Configuration" />}
                module={RMSModules.FLOAT_CONFIGURATION}
                url={`configurations/franchisors/properties/${RMSModules.FLOAT_CONFIGURATION}`}
                propertyIdFromUrl
                toParent={toParentFromSettingsIframe}
              />
            </LazyComponent>
          )}
        />
        <ViewRoute
          exact
          path={`${path}/localisation-configuration`}
          render={() => (
            <LazyComponent>
              <RMSPage
                subscription={RMSSubscriptions.BASE}
                title={<Translate id="Localization_Configuration" />}
                module={RMSModules.LOCALIZATION_CONFIGURATION}
                url={`configurations/franchisors/properties/${RMSModules.LOCALIZATION_CONFIGURATION}`}
                propertyIdFromUrl
                toParent={toParentFromSettingsIframe}
              />
            </LazyComponent>
          )}
        />
        {isShowCollectionSettingsAtPropertyLevelOn && (
          <ViewRoute
            path={`${path}/settings`}
            render={(routeProps) => (
              <LazyComponent>
                <PropertyManagement
                  {...routeProps}
                  baseUrl={routeProps.match.url}
                  entityType="property"
                />
              </LazyComponent>
            )}
          />
        )}
        <ViewRoute
          exact
          path={`${path}/opening-hours`}
          viewId={PortalViewId.PropertiesOpeningHours}
          render={(routeProps) => <PropertyOpeningHoursPage {...(routeProps as any)} />}
        />
        <ViewRoute
          exact
          path={`${path}/dangerzone`}
          render={(routeProps) => <DangerZoneList {...(routeProps as any)} />}
        />
      </Switch>
    </ErrorBoundary>
  );
};

export default PropertySettingsRoutes;
